/**
 * Audio Buffer Underrun Investigation System
 * 
 * Monitors HTML5 audio element buffer health and correlates with ultra-low audio detections
 * to identify buffer underrun events that cause audio dropouts while visualizer continues.
 */

import { audioPerformanceLogger } from './audioPerformanceLogger';

// Buffer monitoring state
let bufferMonitor = {
  isMonitoring: false,
  audioElement: null,
  bufferEvents: [],
  bufferHealth: {
    lastBufferedEnd: 0,
    bufferGap: 0,
    readyState: 0,
    networkState: 0,
    bufferedRanges: []
  },
  underrunDetection: {
    consecutiveUnderruns: 0,
    lastUnderrunTime: 0,
    isInUnderrun: false,
    underrunStartTime: 0
  },
  statistics: {
    totalUnderruns: 0,
    totalStalls: 0,
    totalWaiting: 0,
    totalSuspensions: 0,
    averageUnderrunDuration: 0,
    longestUnderrun: 0
  }
};

/**
 * Initialize buffer monitoring for an audio element
 */
export function initializeBufferMonitor(audioElement) {
  if (!audioElement) {
    console.warn('[BUFFER] No audio element provided for buffer monitoring');
    return;
  }

  console.log('[BUFFER] 🎵 Initializing buffer underrun monitoring');
  audioPerformanceLogger.info('buffer', 'Buffer monitoring initialized');
  
  bufferMonitor.audioElement = audioElement;
  bufferMonitor.isMonitoring = true;
  
  // Reset statistics
  bufferMonitor.statistics = {
    totalUnderruns: 0,
    totalStalls: 0,
    totalWaiting: 0,
    totalSuspensions: 0,
    averageUnderrunDuration: 0,
    longestUnderrun: 0
  };
  
  // Add comprehensive event listeners for buffer-related events
  addBufferEventListeners(audioElement);
  
  // Start periodic buffer health monitoring
  startBufferHealthMonitoring();
  
  return bufferMonitor;
}

/**
 * Add event listeners for all buffer-related audio events
 */
function addBufferEventListeners(audioElement) {
  // Critical buffer events that indicate underruns
  audioElement.addEventListener('waiting', handleWaitingEvent);
  audioElement.addEventListener('stalled', handleStalledEvent);
  audioElement.addEventListener('suspend', handleSuspendEvent);
  
  // Buffer recovery events
  audioElement.addEventListener('canplay', handleCanPlayEvent);
  audioElement.addEventListener('canplaythrough', handleCanPlayThroughEvent);
  audioElement.addEventListener('playing', handlePlayingEvent);
  
  // Network and loading events
  audioElement.addEventListener('loadstart', handleLoadStartEvent);
  audioElement.addEventListener('progress', handleProgressEvent);
  audioElement.addEventListener('loadeddata', handleLoadedDataEvent);
  audioElement.addEventListener('loadedmetadata', handleLoadedMetadataEvent);
  
  // Error events
  audioElement.addEventListener('error', handleErrorEvent);
  audioElement.addEventListener('abort', handleAbortEvent);
  
  console.log('[BUFFER] 📡 Buffer event listeners attached');
  audioPerformanceLogger.debug('buffer', 'Buffer event listeners attached');
}

/**
 * Handle 'waiting' event - most critical for underrun detection
 */
function handleWaitingEvent(event) {
  const timestamp = Date.now();
  const currentTime = event.target.currentTime;
  const buffered = getBufferedInfo(event.target);
  
  bufferMonitor.statistics.totalWaiting++;
  bufferMonitor.underrunDetection.isInUnderrun = true;
  bufferMonitor.underrunDetection.underrunStartTime = timestamp;
  
  console.warn('[BUFFER] 🔴 WAITING EVENT - Buffer underrun detected!', {
    currentTime,
    buffered,
    readyState: event.target.readyState,
    networkState: event.target.networkState
  });
  
  audioPerformanceLogger.error('buffer', 'Buffer underrun - WAITING event', {
    currentTime,
    buffered,
    readyState: event.target.readyState,
    networkState: event.target.networkState,
    timestamp: new Date(timestamp).toISOString(),
    severity: 'BUFFER_UNDERRUN'
  });
  
  logBufferEvent('waiting', {
    currentTime,
    buffered,
    readyState: event.target.readyState,
    networkState: event.target.networkState
  });
}

/**
 * Handle 'stalled' event - network stall that can cause underruns
 */
function handleStalledEvent(event) {
  const timestamp = Date.now();
  const currentTime = event.target.currentTime;
  const buffered = getBufferedInfo(event.target);
  
  bufferMonitor.statistics.totalStalls++;
  
  console.warn('[BUFFER] 🟡 STALLED EVENT - Network stall detected', {
    currentTime,
    buffered,
    readyState: event.target.readyState
  });
  
  audioPerformanceLogger.warn('buffer', 'Network stall - STALLED event', {
    currentTime,
    buffered,
    readyState: event.target.readyState,
    timestamp: new Date(timestamp).toISOString()
  });
  
  logBufferEvent('stalled', {
    currentTime,
    buffered,
    readyState: event.target.readyState
  });
}

/**
 * Handle 'suspend' event - playback suspended due to buffer issues
 */
function handleSuspendEvent(event) {
  const timestamp = Date.now();
  const currentTime = event.target.currentTime;
  const buffered = getBufferedInfo(event.target);
  
  bufferMonitor.statistics.totalSuspensions++;
  
  console.warn('[BUFFER] 🟠 SUSPEND EVENT - Playback suspended', {
    currentTime,
    buffered,
    readyState: event.target.readyState
  });
  
  audioPerformanceLogger.warn('buffer', 'Playback suspended - SUSPEND event', {
    currentTime,
    buffered,
    readyState: event.target.readyState,
    timestamp: new Date(timestamp).toISOString()
  });
  
  logBufferEvent('suspend', {
    currentTime,
    buffered,
    readyState: event.target.readyState
  });
}

/**
 * Handle 'canplay' event - buffer recovered enough to start playing
 */
function handleCanPlayEvent(event) {
  const timestamp = Date.now();
  const currentTime = event.target.currentTime;
  const buffered = getBufferedInfo(event.target);
  
  // Calculate underrun duration if we were in an underrun
  if (bufferMonitor.underrunDetection.isInUnderrun) {
    const underrunDuration = timestamp - bufferMonitor.underrunDetection.underrunStartTime;
    bufferMonitor.underrunDetection.isInUnderrun = false;
    bufferMonitor.statistics.totalUnderruns++;
    
    // Update statistics
    if (underrunDuration > bufferMonitor.statistics.longestUnderrun) {
      bufferMonitor.statistics.longestUnderrun = underrunDuration;
    }
    
    const totalDuration = (bufferMonitor.statistics.averageUnderrunDuration * (bufferMonitor.statistics.totalUnderruns - 1)) + underrunDuration;
    bufferMonitor.statistics.averageUnderrunDuration = totalDuration / bufferMonitor.statistics.totalUnderruns;
    
    console.log('[BUFFER] 🟢 CANPLAY EVENT - Buffer recovered', {
      currentTime,
      buffered,
      underrunDuration: `${underrunDuration}ms`,
      readyState: event.target.readyState
    });
    
    audioPerformanceLogger.info('buffer', 'Buffer recovered - CANPLAY event', {
      currentTime,
      buffered,
      underrunDuration,
      readyState: event.target.readyState,
      timestamp: new Date(timestamp).toISOString()
    });
  }
  
  logBufferEvent('canplay', {
    currentTime,
    buffered,
    readyState: event.target.readyState
  });
}

/**
 * Handle other recovery events
 */
function handleCanPlayThroughEvent(event) {
  logBufferEvent('canplaythrough', {
    currentTime: event.target.currentTime,
    buffered: getBufferedInfo(event.target),
    readyState: event.target.readyState
  });
}

function handlePlayingEvent(event) {
  logBufferEvent('playing', {
    currentTime: event.target.currentTime,
    buffered: getBufferedInfo(event.target),
    readyState: event.target.readyState
  });
}

function handleLoadStartEvent(event) {
  logBufferEvent('loadstart', {
    currentTime: event.target.currentTime,
    readyState: event.target.readyState,
    networkState: event.target.networkState
  });
}

function handleProgressEvent(event) {
  logBufferEvent('progress', {
    currentTime: event.target.currentTime,
    buffered: getBufferedInfo(event.target),
    readyState: event.target.readyState
  });
}

function handleLoadedDataEvent(event) {
  logBufferEvent('loadeddata', {
    currentTime: event.target.currentTime,
    buffered: getBufferedInfo(event.target),
    readyState: event.target.readyState
  });
}

function handleLoadedMetadataEvent(event) {
  logBufferEvent('loadedmetadata', {
    duration: event.target.duration,
    readyState: event.target.readyState
  });
}

function handleErrorEvent(event) {
  console.error('[BUFFER] ❌ AUDIO ERROR', event.target.error);
  audioPerformanceLogger.error('buffer', 'Audio element error', {
    error: event.target.error,
    timestamp: new Date().toISOString()
  });
  
  logBufferEvent('error', {
    error: event.target.error,
    readyState: event.target.readyState
  });
}

function handleAbortEvent(event) {
  logBufferEvent('abort', {
    currentTime: event.target.currentTime,
    readyState: event.target.readyState
  });
}

/**
 * Get detailed buffer information from audio element
 */
function getBufferedInfo(audioElement) {
  if (!audioElement.buffered) return { ranges: [], total: 0, currentBuffer: 0 };
  
  const buffered = audioElement.buffered;
  const currentTime = audioElement.currentTime;
  const ranges = [];
  let totalBuffered = 0;
  let currentBuffer = 0;
  
  for (let i = 0; i < buffered.length; i++) {
    const start = buffered.start(i);
    const end = buffered.end(i);
    ranges.push({ start, end, duration: end - start });
    totalBuffered += (end - start);
    
    // Check if current time is in this range
    if (currentTime >= start && currentTime <= end) {
      currentBuffer = end - currentTime;
    }
  }
  
  return {
    ranges,
    totalBuffered,
    currentBuffer,
    rangeCount: buffered.length
  };
}

/**
 * Log buffer event with timestamp
 */
function logBufferEvent(eventType, data) {
  const event = {
    type: eventType,
    timestamp: Date.now(),
    data
  };
  
  bufferMonitor.bufferEvents.push(event);
  
  // Keep only last 100 events to prevent memory issues
  if (bufferMonitor.bufferEvents.length > 100) {
    bufferMonitor.bufferEvents = bufferMonitor.bufferEvents.slice(-100);
  }
}

/**
 * Start periodic buffer health monitoring
 */
function startBufferHealthMonitoring() {
  setInterval(() => {
    if (!bufferMonitor.isMonitoring || !bufferMonitor.audioElement) return;
    
    const audioElement = bufferMonitor.audioElement;
    const currentTime = audioElement.currentTime;
    const buffered = getBufferedInfo(audioElement);
    
    // Update buffer health metrics
    bufferMonitor.bufferHealth = {
      lastBufferedEnd: buffered.ranges.length > 0 ? buffered.ranges[buffered.ranges.length - 1].end : 0,
      bufferGap: buffered.currentBuffer,
      readyState: audioElement.readyState,
      networkState: audioElement.networkState,
      bufferedRanges: buffered.ranges
    };
    
    // Check for buffer health issues
    if (buffered.currentBuffer < 5 && !audioElement.paused) { // Less than 5 seconds buffered ahead
      console.warn('[BUFFER] ⚠️ Low buffer detected', {
        currentTime,
        bufferAhead: buffered.currentBuffer,
        totalBuffered: buffered.totalBuffered,
        readyState: audioElement.readyState
      });
      
      audioPerformanceLogger.warn('buffer', 'Low buffer warning', {
        currentTime,
        bufferAhead: buffered.currentBuffer,
        totalBuffered: buffered.totalBuffered,
        readyState: audioElement.readyState,
        timestamp: new Date().toISOString()
      });
    }
  }, 1000); // Check every second
}

/**
 * Correlate ultra-low audio detection with buffer events
 */
export function correlateWithUltraLowAudio(audioLevel, samples) {
  if (!bufferMonitor.isMonitoring) return;
  
  const currentTime = Date.now();
  const recentBufferEvents = bufferMonitor.bufferEvents.filter(
    event => currentTime - event.timestamp < 2000 // Events in last 2 seconds
  );
  
  // Check if ultra-low audio correlates with recent buffer events
  const hasRecentWaiting = recentBufferEvents.some(event => event.type === 'waiting');
  const hasRecentStalled = recentBufferEvents.some(event => event.type === 'stalled');
  const hasRecentSuspend = recentBufferEvents.some(event => event.type === 'suspend');
  
  if (audioLevel < 20 && (hasRecentWaiting || hasRecentStalled || hasRecentSuspend)) {
    console.error('[BUFFER] 🔥 CORRELATION DETECTED - Ultra-low audio correlates with buffer event!', {
      audioLevel,
      samples,
      recentEvents: recentBufferEvents.map(e => e.type),
      bufferHealth: bufferMonitor.bufferHealth,
      isInUnderrun: bufferMonitor.underrunDetection.isInUnderrun
    });
    
    audioPerformanceLogger.error('buffer', 'Ultra-low audio correlates with buffer underrun', {
      audioLevel,
      samples,
      recentEvents: recentBufferEvents,
      bufferHealth: bufferMonitor.bufferHealth,
      timestamp: new Date().toISOString(),
      severity: 'CONFIRMED_BUFFER_UNDERRUN'
    });
    
    return {
      isCorrelated: true,
      bufferEvents: recentBufferEvents,
      bufferHealth: bufferMonitor.bufferHealth
    };
  }
  
  return {
    isCorrelated: false,
    bufferEvents: recentBufferEvents,
    bufferHealth: bufferMonitor.bufferHealth
  };
}

/**
 * Get current buffer monitoring report
 */
export function getBufferMonitoringReport() {
  if (!bufferMonitor.isMonitoring) {
    return { isMonitoring: false };
  }
  
  const recentEvents = bufferMonitor.bufferEvents.slice(-10); // Last 10 events
  
  return {
    isMonitoring: true,
    bufferHealth: bufferMonitor.bufferHealth,
    underrunDetection: bufferMonitor.underrunDetection,
    statistics: bufferMonitor.statistics,
    recentEvents,
    audioElement: {
      currentTime: bufferMonitor.audioElement?.currentTime || 0,
      duration: bufferMonitor.audioElement?.duration || 0,
      readyState: bufferMonitor.audioElement?.readyState || 0,
      networkState: bufferMonitor.audioElement?.networkState || 0,
      paused: bufferMonitor.audioElement?.paused || true
    }
  };
}

/**
 * Reset buffer monitoring statistics
 */
export function resetBufferMonitoring() {
  console.log('[BUFFER] 🔄 Resetting buffer monitoring statistics');
  
  bufferMonitor.bufferEvents = [];
  bufferMonitor.underrunDetection = {
    consecutiveUnderruns: 0,
    lastUnderrunTime: 0,
    isInUnderrun: false,
    underrunStartTime: 0
  };
  bufferMonitor.statistics = {
    totalUnderruns: 0,
    totalStalls: 0,
    totalWaiting: 0,
    totalSuspensions: 0,
    averageUnderrunDuration: 0,
    longestUnderrun: 0
  };
  
  audioPerformanceLogger.info('buffer', 'Buffer monitoring reset');
}

/**
 * Stop buffer monitoring and cleanup
 */
export function stopBufferMonitoring() {
  if (!bufferMonitor.isMonitoring || !bufferMonitor.audioElement) return;
  
  console.log('[BUFFER] 🛑 Stopping buffer monitoring');
  
  // Remove all event listeners
  const audioElement = bufferMonitor.audioElement;
  audioElement.removeEventListener('waiting', handleWaitingEvent);
  audioElement.removeEventListener('stalled', handleStalledEvent);
  audioElement.removeEventListener('suspend', handleSuspendEvent);
  audioElement.removeEventListener('canplay', handleCanPlayEvent);
  audioElement.removeEventListener('canplaythrough', handleCanPlayThroughEvent);
  audioElement.removeEventListener('playing', handlePlayingEvent);
  audioElement.removeEventListener('loadstart', handleLoadStartEvent);
  audioElement.removeEventListener('progress', handleProgressEvent);
  audioElement.removeEventListener('loadeddata', handleLoadedDataEvent);
  audioElement.removeEventListener('loadedmetadata', handleLoadedMetadataEvent);
  audioElement.removeEventListener('error', handleErrorEvent);
  audioElement.removeEventListener('abort', handleAbortEvent);
  
  bufferMonitor.isMonitoring = false;
  bufferMonitor.audioElement = null;
  
  audioPerformanceLogger.info('buffer', 'Buffer monitoring stopped');
}
